import { motion } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useState } from 'react';

const defaultFAQs = [
  {
    category: 'General',
    questions: [
      {
        q: 'What is GreenLens?',
        a: 'GreenLens is an AI-powered plant identification service that helps you identify plants, get care instructions, and learn more about your flora. Simply take a photo or upload an image of a plant, and our AI will analyze it to provide detailed information.'
      },
      {
        q: 'How accurate is the plant identification?',
        a: 'Our AI model has a high accuracy rate for common plants. However, for rare species or unclear images, we provide tentative identifications and always recommend verifying with local experts for critical decisions.'
      },
      {
        q: 'Can I use GreenLens on my mobile device?',
        a: "Yes! GreenLens works on any device with a modern web browser. You can take photos directly with your device's camera or upload existing images."
      }
    ]
  },
  {
    category: 'Credits & Pricing',
    questions: [
      {
        q: 'How do credits work?',
        a: 'Credits are used for plant identifications. Free accounts get 5 credits per month, while paid plans offer more credits and features. Each identification uses 1 credit.'
      },
      {
        q: 'When do my credits expire?',
        a: 'Credits reset at the beginning of each month. Unused credits from the previous month do not carry over. Premium plans get their credits refreshed monthly on their billing date.'
      },
      {
        q: 'Can I buy additional credits?',
        a: 'Yes! You can purchase additional credits at any time. Extra credits cost ₹10 per credit and are valid until the end of the current month.'
      },
      {
        q: 'What payment methods do you accept?',
        a: 'We accept all major credit cards, debit cards, and popular digital payment methods. All payments are processed securely through our payment provider.'
      }
    ]
  },
  {
    category: 'Features & Usage',
    questions: [
      {
        q: 'What information do I get with each identification?',
        a: 'Each identification includes: common and scientific names, detailed plant description, key identifying characteristics, basic care requirements, and any special notes or warnings about the plant.'
      },
      {
        q: 'Can I save my identification history?',
        a: 'Yes! All identifications are automatically saved to your account. You can access your history anytime from your dashboard.'
      },
      {
        q: 'What image types are supported?',
        a: 'We support JPEG and PNG image formats. For best results, ensure your image is clear, well-lit, and shows the key features of the plant (leaves, flowers, overall structure).'
      }
    ]
  },
  {
    category: 'Account & Support',
    questions: [
      {
        q: 'How do I reset my password?',
        a: 'You can reset your password by clicking "Forgot password?" on the sign-in page. We\'ll send you an email with instructions to create a new password.'
      },
      {
        q: 'Is my data secure?',
        a: 'Yes! We use industry-standard encryption and security practices to protect your data. Your images and personal information are never shared without your consent.'
      },
      {
        q: 'How can I get help if I have a problem?',
        a: "You can contact our support team through the help center, and we'll respond as quickly as possible. Premium users get priority support."
      }
    ]
  }
];

function FAQItem({ question, answer, isOpen, onClick }) {
  return (
    <motion.div
      initial={false}
      animate={{
        backgroundColor: isOpen ? 'rgb(240 253 244)' : 'rgb(255 255 255)'
      }}
      className="border rounded-lg overflow-hidden"
    >
      <button
        onClick={onClick}
        className="w-full px-6 py-4 text-left flex items-center justify-between gap-4"
      >
        <span className="font-medium text-gray-900">{question}</span>
        <motion.div
          initial={false}
          animate={{ rotate: isOpen ? 180 : 0 }}
          className="flex-shrink-0"
        >
          <ChevronDown className="w-5 h-5 text-gray-500" />
        </motion.div>
      </button>
      <motion.div
        initial="collapsed"
        animate={isOpen ? 'open' : 'collapsed'}
        variants={{
          open: { opacity: 1, height: 'auto', marginBottom: 16 },
          collapsed: { opacity: 0, height: 0, marginBottom: 0 }
        }}
        transition={{ duration: 0.3 }}
        className="px-6 overflow-hidden"
      >
        <p className="text-gray-600">{answer}</p>
      </motion.div>
    </motion.div>
  );
}

export function FAQ({
  faqs = defaultFAQs,
  title = 'Frequently Asked Questions'
}) {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-12"
      >
        <div className="inline-flex items-center justify-center p-2 bg-emerald-100 rounded-full mb-4">
          <HelpCircle className="w-6 h-6 text-emerald-600" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">{title}</h2>
        <p className="text-xl text-gray-600">
          Find answers to commonly asked questions
        </p>
      </motion.div>

      <div className="max-w-3xl mx-auto px-4">
        {faqs.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-12 last:mb-0">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              {category.category}
            </h3>
            <div className="space-y-4">
              {category.questions.map((faq, questionIndex) => (
                <FAQItem
                  key={questionIndex}
                  question={faq.q}
                  answer={faq.a}
                  isOpen={openItems[`${categoryIndex}-${questionIndex}`]}
                  onClick={() => toggleItem(categoryIndex, questionIndex)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
